import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCourse } from '@/contexts/CourseContext';
import MetadataStepShell from '@/components/step-shells/MetadataStepShell';

// localStorage helpers for preview persistence
const PREVIEW_STORAGE_KEY = 'step1_preview_shown_';

function getPreviewShown(courseId) {
  if (!courseId) return false;
  try {
    return localStorage.getItem(PREVIEW_STORAGE_KEY + courseId) === 'true';
  } catch {
    return false;
  }
}

function setPreviewShown(courseId) {
  if (!courseId) return;
  try {
    localStorage.setItem(PREVIEW_STORAGE_KEY + courseId, 'true');
  } catch {
    // Ignore storage errors
  }
}

export default function Step1Metadata() {
  const { t } = useTranslation();
  const { course, dispatch, importMode, updateCourse, chatHistory } = useCourse();
  const [showPreview, setShowPreview] = useState(false);
  const [initializedCourseId, setInitializedCourseId] = useState(null);
  const [initialCourse, setInitialCourse] = useState(null);

  // Initialize Step 1 when component mounts - show hardcoded welcome message
  // Only send greeting once per course ID, even if course object reference changes
  const courseId = course?.id;
  const courseLanguage = course?.language || 'English';
  
  useEffect(() => {
    // Only initialize once per course ID (not on every course property change)
    if (courseId && initializedCourseId !== courseId) {
      // Check if greeting message already exists in chat history to prevent duplicates
      const hasGreeting = chatHistory.some(msg => 
        msg.role === 'assistant' && (
          msg.text?.includes("I'm **Coby**") || 
          msg.text?.includes("Είμαι ο **Coby**") ||
          msg.text?.includes("Hello!") ||
          msg.text?.includes("Γεια σας!")
        )
      );

      // Only send greeting if it doesn't already exist
      if (!hasGreeting) {
        const isGreek = courseLanguage.toLowerCase() === 'greek' || courseLanguage.toLowerCase() === 'ελληνικά';

        const greetingMessage = isGreek
          ? "**Γεια σας!** Είμαι ο **Coby**, ο βοηθός σας στον σχεδιασμό μαθημάτων.\n\nΜαζί θα περάσουμε από 4 βήματα: θα ορίσουμε τα βασικά στοιχεία του μαθήματος, θα δημιουργήσουμε μαθησιακούς στόχους, θα σχεδιάσουμε δραστηριότητες, και τέλος θα ολοκληρώσουμε την τελική επισκόπηση.\n\nΣε αυτό το πρώτο βήμα, θα συγκεντρώσουμε μερικές βασικές πληροφορίες για να χτίσουμε τη βάση του μαθήματός σας.\n\n**Ας ξεκινήσουμε!** Τι θεματολογία θα έχει το μάθημά σας;"
          : "**Hello!** I'm **Coby**, your course design assistant.\n\nTogether we'll go through 4 steps: we'll define the course basics, create learning objectives, design engaging activities, and finally complete a review.\n\nIn this first step, we'll gather some foundational information to build a solid base for your course.\n\n**Let's get started!** What will your course be about?";

        dispatch({ type: 'ADD_CHAT_MESSAGE', payload: {
          role: 'assistant',
          text: greetingMessage
        } });
      }
      
      // Mark this course ID as initialized (even if greeting already existed)
      setInitializedCourseId(courseId);
    }
  }, [courseId, initializedCourseId, chatHistory, dispatch, courseLanguage]);

  // Store initial course state on mount (import mode)
  useEffect(() => {
    if (importMode && course && !initialCourse) {
      setInitialCourse({
        title: course.title,
        totalHours: course.totalHours,
        educationalLevel: course.educationalLevel,
        grade: course.grade,
        subjectArea: course.subjectArea,
        description: course.description
      });
    }
  }, [importMode, course, initialCourse]);

  // Check if we have the minimum info to show preview
  // Uses localStorage to persist "preview was shown" state across component remounts
  useEffect(() => {
    // In import mode, always show preview if we have at least title OR description
    if (importMode && course) {
      const hasAnyContent = course.title || (course.description && course.description.trim() !== '');
      setShowPreview(hasAnyContent);
      if (hasAnyContent) setPreviewShown(courseId);
      return;
    }

    // In regular (build) mode:
    // Once preview has been shown, NEVER hide it (even if user deletes a field)
    if (getPreviewShown(courseId)) {
      setShowPreview(true);
      return;
    }

    // Show preview when ALL fields are complete for the first time
    if (!importMode && course) {
      const allFieldsComplete =
        course.title &&
        course.totalHours > 0 &&
        course.educationalLevel &&
        course.grade &&
        course.subjectArea &&
        course.description &&
        course.description.trim() !== '';

      if (allFieldsComplete) {
        setPreviewShown(courseId);
        setShowPreview(true);
      }
    }
  }, [course, importMode, courseId]);

  const handleEdit = async (field, value) => {
    if (!course) return;
    const updates = { [field]: value };
    await updateCourse(updates);
  };

  return (
    <MetadataStepShell
      showPreview={showPreview}
      metadata={course || {}}
      headerTitle={importMode ? t('step1.enhanceCourseInfo') : t('step1.tellAboutCourse')}
      headerSubtitle={showPreview 
        ? t('step1.continueRefining')
        : (importMode ? t('step1.workWithCoby') : t('step1.chatToGetStarted'))
      }
      onEdit={handleEdit}
    />
  );
}